import {
  Component,
  Input,
  ViewEncapsulation,
  forwardRef,
  ViewChild,
  OnDestroy,
} from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormArray,
  AbstractControl,
  Validators,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'
import {debounceTime, takeUntil} from 'rxjs/operators'

import {Subject} from 'rxjs'

import {AdvancedConfiguration} from '@app/core/services/rest-api'
import {NgbNav} from '@ng-bootstrap/ng-bootstrap'

interface SettingTreeNode {
  name: string
  code?: string
  subcats?: SettingTreeNode[]
  control: AbstractControl
  copyControl?: AbstractControl
}

@Component({
  selector: 'advanced-configuration',
  templateUrl: './advanced-configuration-form.component.html',
  styleUrls: ['./advanced-configuration-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdvancedConfigurationFormComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class AdvancedConfigurationFormComponent
  implements ControlValueAccessor, OnDestroy
{
  configuration: AdvancedConfiguration
  @Input() referenceConfiguration: AdvancedConfiguration
  @Input() copyConfig: boolean = false
  @Input() ignoreCopyKeys: string[] = []
  @Input() referencePrefix: string = 'Terminal value: '
  @Input() apollo: boolean = false

  form: FormGroup
  copyForm: FormGroup
  defaultCopyForm = {}

  addSettingForm: FormGroup
  showAddSettingForm: boolean

  @ViewChild('tabmain', {static: true}) tabmain: NgbNav
  @ViewChild('tabsub', {static: true}) tabsub: NgbNav

  settingsTree: SettingTreeNode[]

  _changeFn: (_: any) => void

  mainCategories = [
    {code: '0', name: 'PTOS'},
    {code: '1', name: 'PTOS 2'},
    {code: '2', name: 'EMV Config'},
    {code: '2049', name: 'Payter'},
    {code: '2305', name: 'Cashless'},
    {code: '4097', name: 'EMV App'},
    {code: '4353', name: 'OV Fiets'},
    {code: '4609', name: 'Creditcall'},
    {code: '4865', name: 'EMV Sim'},
    {code: '5121', name: 'PayPlaza'},
    {code: '5889', name: 'CCV'},
    {code: '6145', name: 'Monek'},
    {code: '6401', name: 'BKM'},
    {code: '8193', name: 'EMS'},
    {code: '8449', name: 'Allego'},
  ]

  constructor(private fb: FormBuilder) {}

  private end$: Subject<void> = new Subject<void>()
  ngOnDestroy() {
    this.end$.next()
    this.end$.complete()
  }

  buildForm() {
    const fb = this.fb

    //prepare the copyForm
    this.copyForm = fb.group({})

    let control = fb.control(this.configuration.firmware, Validators.required)
    let group: FormGroup<any> = fb.group({
      category: fb.control(null),
      subcategory: fb.control(null),
    })

    if (this.copyConfig) {
      let firmwareDifferent = this.referenceConfiguration
        ? this.configuration.firmware != this.referenceConfiguration.firmware
        : true
      let copyControl = fb.control(firmwareDifferent)

      if (!firmwareDifferent) {
        control.disable()
      }

      copyControl.valueChanges.subscribe((value) => {
        if (value) {
          control.enable()
        } else {
          control.disable()
        }
      })
      this.copyForm.addControl('firmware', copyControl)
    }

    group.addControl('firmware', control)

    const resources: FormArray = fb.array([])
    let i = 0
    for (const resource of this.configuration.resources) {
      let control = fb.control(resource)
      resources.push(control)

      if (this.copyConfig) {
        let resourceExists = this.referenceConfiguration
          ? this.referenceConfiguration.resources.includes(resource)
          : false
        let copyControl = fb.control(!resourceExists)
        if (resourceExists) {
          control.disable()
        }
        copyControl.valueChanges.subscribe((value) => {
          if (value) {
            control.enable()
          } else {
            control.disable()
          }
        })
        this.copyForm.addControl('resource' + i++, copyControl)
      }
    }

    group.addControl('resources', resources)

    const settings: FormGroup = fb.group({})

    group.addControl('settings', settings)

    this.settingsTree = []
    Object.keys(this.configuration.settings)
      .sort((a, b) => a.localeCompare(b))
      .forEach((key: string) => {
        const parts = key.split('.')
        const catCode = parts[0]
        const catName = this.apollo ? catCode : this.translate(catCode)
        let cat: SettingTreeNode = this.settingsTree.find(
          (node) => node.name === catName,
        )
        if (!cat) {
          cat = {
            name: catName,
            code: catCode,
            subcats: [],
            control: fb.group({}),
          }
          settings.addControl(catCode, cat.control)
          this.settingsTree.push(cat)
        }

        const settingName = this.apollo ? parts[1] : parts[2]
        const setting: SettingTreeNode = {
          name: settingName,
          control: fb.control(this.configuration.settings[key]),
        }

        let subcat: SettingTreeNode = null
        if (this.apollo) {
          // Apollo terminals have 1 layer

          ;(<FormGroup>cat.control).addControl(settingName, setting.control)
          cat.subcats.push(setting)
        } else {
          // Apollo config has no subcat
          const subcatName = parts[1]
          subcat = cat.subcats.find((node) => node.name === subcatName)
          if (!subcat) {
            subcat = {
              name: subcatName,
              subcats: [],
              control: fb.group({}),
            }
            ;(<FormGroup>cat.control).addControl(subcatName, subcat.control)
            cat.subcats.push(subcat)
          }

          ;(<FormGroup>subcat.control).addControl(settingName, setting.control)

          subcat.subcats.push(setting)
        }

        if (this.copyConfig) {
          //manage copy settings
          let diff = this.getValueDifference(setting, subcat, cat)
          let enableCopy =
            !this.ignoreCopyKeys.includes(key) &&
            (diff != null || this.referenceConfiguration == null)

          if (!enableCopy) {
            setting.control.disable()
          }
          let copyControl = fb.control(enableCopy)
          setting.copyControl = copyControl
          copyControl.valueChanges.subscribe((value) => {
            if (value) {
              setting.control.enable()
            } else {
              setting.control.disable()
            }
          })
          this.copyForm.addControl(key, copyControl)
        }
      })

    group.valueChanges
      .pipe(debounceTime(100), takeUntil(this.end$))
      .subscribe(this.onChanges)

    return group
  }

  createDefaultCopyForm() {
    Object.entries(this.copyForm.controls).forEach(([key, control]) => {
      if (control.value) {
        this.defaultCopyForm[key] = true
      }
    })
  }

  buildAddSettingForm(fb: FormBuilder): FormGroup {
    return fb.group({
      key: fb.control(null, Validators.required),
      value: fb.control(null, Validators.required),
      cat: fb.control(null),
      subcat: fb.control(null),
    })
  }

  translate(name: string) {
    switch (name) {
      case '0':
        return 'PTOS'
      case '1':
        return 'PTOS 2'
      case '2':
        return 'EMV Config'
      case '2049':
        return 'Payter'
      case '2305':
        return 'Cashless'
      case '4097':
        return 'EMV App'
      case '4353':
        return 'OV Fiets'
      case '4609':
        return 'Creditcall'
      case '4865':
        return 'EMV Sim'
      case '5121':
        return 'PayPlaza'
      case '5889':
        return 'CCV'
      case '6145':
        return 'Monek'
      case '6401':
        return 'BKM'
      case '8193':
        return 'EMS'
      case '8449':
        return 'Allego'
      default:
        return 'Other'
    }
  }

  getFirmwareDifference(): string {
    if (this.referenceConfiguration) {
      let refValue = this.referenceConfiguration.firmware || null
      if (refValue !== this.form.controls['firmware'].value) {
        if (refValue == null) {
          return '<undefined>'
        }
        return this.referenceConfiguration.firmware
      }
    }
    return null
  }

  getSettingKey(
    setting: SettingTreeNode,
    subcat: SettingTreeNode,
    cat: SettingTreeNode,
  ): string {
    if (this.apollo) {
      return cat.code + '.' + setting.name
    }
    return (
      cat.code.toUpperCase() +
      '.' +
      subcat.name.toUpperCase() +
      '.' +
      setting.name
    )
  }

  getValueDifference(
    setting: SettingTreeNode,
    subcat: SettingTreeNode,
    cat: SettingTreeNode,
  ): string {
    if (this.referenceConfiguration) {
      let value = setting.control.value
      let key = this.getSettingKey(setting, subcat, cat)
      let referenceValue = this.referenceConfiguration.settings[key]
      if (referenceValue !== value) {
        if (referenceValue === '') {
          return '<empty>'
        }
        if (!referenceValue) {
          return '<no value>'
        }
        return referenceValue
      }
    }
    return null
  }

  getChangeCount(subcat: SettingTreeNode, cat: SettingTreeNode): number {
    let changes = 0
    if (this.referenceConfiguration) {
      if (!this.apollo) {
        for (let setting of subcat.subcats) {
          if (this.getValueDifference(setting, subcat, cat) !== null) {
            changes++
          }
        }
      }
    }
    return changes
  }

  getCatChangeCount(cat: SettingTreeNode): number {
    let changes = 0
    if (this.referenceConfiguration) {
      if (this.apollo) {
        for (let setting of cat.subcats) {
          if (this.getValueDifference(setting, null, cat) !== null) {
            changes++
          }
        }
      } else {
        for (let subcat of cat.subcats) {
          changes += this.getChangeCount(subcat, cat)
        }
      }
    }
    return changes
  }

  addResource() {
    const resources: FormArray = <FormArray>this.form.get('resources')
    const ctrl = this.fb.control(null)

    resources.push(ctrl)
  }

  removeResource(control) {
    const resources: FormArray = <FormArray>this.form.get('resources')
    const index = resources.controls.indexOf(control)
    if (index >= 0) {
      resources.removeAt(index)
    }
  }

  showAddSetting(cat, subcat) {
    if (!this.addSettingForm) {
      this.addSettingForm = this.buildAddSettingForm(this.fb)
    }
    this.addSettingForm.get('cat').setValue(cat)
    this.addSettingForm.get('subcat').setValue(subcat)

    this.showAddSettingForm = true
  }

  hideAddSettingForm() {
    this.showAddSettingForm = false
  }

  addSetting() {
    const key = this.addSettingForm.get('key').value
    const value = this.addSettingForm.get('value').value
    const cat = this.addSettingForm.get('cat').value
    const subcat = this.addSettingForm.get('subcat').value

    let settingNode: SettingTreeNode = null
    if (this.apollo) {
      //apollo config is 1 layer deep
      const catNode: SettingTreeNode = this.settingsTree.find(
        (node) => node.code === cat,
      )

      settingNode = {
        name: key,
        control: this.fb.control(value),
      }
      ;(<FormGroup>catNode.control).addControl(key, settingNode.control)

      catNode.subcats.push(settingNode)
    } else {
      // P6X config is 2 layers deep
      const subcatNode: SettingTreeNode = this.settingsTree
        .find((node) => node.code === cat)
        .subcats.find((node) => node.name === subcat)

      settingNode = {
        name: key,
        control: this.fb.control(value),
      }
      ;(<FormGroup>subcatNode.control).addControl(key, settingNode.control)

      subcatNode.subcats.push(settingNode)
    }

    if (this.copyConfig) {
      let copyControl = this.fb.control(true)
      settingNode.copyControl = copyControl
      copyControl.valueChanges.subscribe(() => {
        if (settingNode.control.disabled) {
          settingNode.control.enable()
        } else {
          settingNode.control.disable()
        }
      })
      this.copyForm.addControl(key, copyControl)
    }

    this.addSettingForm.reset()
    this.showAddSettingForm = false
    this.onChanges(this.form.value)
  }

  removeSetting(cat: SettingTreeNode, setting: SettingTreeNode) {
    const index = cat.subcats.indexOf(setting)
    if (index >= 0) {
      cat.subcats.splice(index, 1)
    }
    ;(<FormGroup>cat.control).removeControl(setting.name)
    this.onChanges(this.form.value)
  }

  toggleCopyControl() {
    const bool = this.isPartiallySelected()
    Object.keys(this.defaultCopyForm).forEach((key) =>
      this.copyForm.controls[key].setValue(!bool),
    )
  }

  isPartiallySelected() {
    for (const [key, value] of Object.entries(this.defaultCopyForm)) {
      if (this.copyForm.controls[key].value === value) {
        return true
      }
    }
    return false
  }

  public onChanges = (values: any) => {
    const settings = {}

    if (this.apollo) {
      if (values.hasOwnProperty('settings')) {
        Object.keys(values['settings']).forEach((cat) => {
          Object.keys(values['settings'][cat]).forEach((name) => {
            const key = cat + '.' + name
            settings[key] = values['settings'][cat][name]
          })
        })
      }
    } else {
      if (values.hasOwnProperty('settings')) {
        Object.keys(values['settings']).forEach((cat) => {
          Object.keys(values['settings'][cat]).forEach((subcat) => {
            Object.keys(values['settings'][cat][subcat]).forEach((name) => {
              const key =
                cat.toUpperCase() +
                '.' +
                subcat.toUpperCase() +
                '.' +
                name.toUpperCase()
              settings[key] = values['settings'][cat][subcat][name]
            })
          })
        })
      }
    }

    const config: AdvancedConfiguration = {
      firmware: values['firmware'],
      resources: values['resources'],
      settings: settings,
    }

    if (this._changeFn) {
      this._changeFn(config)
    }
  }

  writeValue(configuration: AdvancedConfiguration) {
    if (configuration === null) {
      this.configuration = {
        firmware: '',
        resources: [],
        settings: {},
      }
    } else {
      this.configuration = configuration
    }
    this.form = this.buildForm()
    if (this.copyConfig) {
      this.createDefaultCopyForm()
    }

    //force changes on writevalue
    if (this.copyConfig) {
      this.onChanges(this.form.value)
    }
  }

  registerOnChange(fn: any) {
    this._changeFn = fn
  }

  registerOnTouched(fn: any) {
    this.form.statusChanges.pipe(takeUntil(this.end$)).subscribe(() => {
      // TODO compare to before status changed?
      if (this.form.dirty) {
        fn()
      }
    })
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.form.disable()
    } else {
      this.form.enable()
    }
  }

  addCategory() {
    const code = this.apollo
      ? this.form.value.category
      : this.form.value.category.code
    const name = this.apollo ? code : this.form.value.category.name

    const subcats = []

    const cat = {
      name: name,
      code: code,
      subcats: subcats,
      control: this.fb.group({}),
    }
    ;(<FormGroup>this.form.controls.settings).addControl(cat.code, cat.control)
    this.settingsTree.push(cat)
    this.form.controls.category.reset()

    return cat.name
  }

  addSubCategory(cat: SettingTreeNode) {
    const subcat = {
      name: this.form.value.subcategory,
      code: this.form.value.subcategory,
      subcats: [],
      control: this.fb.group({}),
    }
    ;(<FormGroup>cat.control).addControl(subcat.code, subcat.control)
    cat.subcats.push(subcat)
    this.form.controls.subcategory.reset()

    return subcat.name
  }

  catFilter = (cat) => {
    let node = this.settingsTree.find((node) => node.code === cat.code)
    return node === undefined
  }
}
